/**
 * Verification runner.
 *
 * Executes every stored case against the production calculation functions and
 * reports pass/fail. Development-status and independent-status results are
 * counted separately and never merged into a single headline number.
 */

import type { VerificationCase, VerificationCaseResult } from '../types';
import { calculateAAF, calculateAATDays } from './acceleratedAging';

export function runCase(testCase: VerificationCase): VerificationCaseResult {
  const actualAAF = calculateAAF(testCase.q10, testCase.acceleratedTempC, testCase.realTimeTempC);
  const actualAATDays = calculateAATDays(testCase.realTimeDays, actualAAF);

  const aafDelta = Math.abs(actualAAF - testCase.expectedAAF);
  const aatDelta = Math.abs(actualAATDays - testCase.expectedAATDays);

  const passed =
    Number.isFinite(actualAAF) &&
    Number.isFinite(actualAATDays) &&
    aafDelta <= testCase.toleranceAAF &&
    aatDelta <= testCase.toleranceAATDays;

  return { testCase, actualAAF, actualAATDays, aafDelta, aatDelta, passed };
}

export function runCases(cases: VerificationCase[]): VerificationCaseResult[] {
  return cases.map(runCase);
}

export interface VerificationSummary {
  developmentTotal: number;
  developmentPassed: number;
  independentTotal: number;
  independentPassed: number;
  /** True only when at least one genuinely independent case exists. */
  hasIndependentData: boolean;
  /** The single sentence the interface is allowed to show as headline status. */
  headline: string;
}

export function summarise(results: VerificationCaseResult[]): VerificationSummary {
  const dev = results.filter((r) => r.testCase.status === 'development');
  const ind = results.filter((r) => r.testCase.status === 'independent');

  const developmentPassed = dev.filter((r) => r.passed).length;
  const independentPassed = ind.filter((r) => r.passed).length;
  const hasIndependentData = ind.length > 0;

  const headline = hasIndependentData
    ? `${independentPassed} / ${ind.length} independently verified reference cases passed`
    : 'Independent verification pending';

  return {
    developmentTotal: dev.length,
    developmentPassed,
    independentTotal: ind.length,
    independentPassed,
    hasIndependentData,
    headline,
  };
}
